import type { HttpResponse } from '@wix/yoshi-flow-editor';
import { queryPosts } from '@wix/ambassador-blog-v3-post/http';
import {
  PostFieldField,
  type PlatformQuery,
  type QueryPostsResponse,
} from '@wix/ambassador-blog-v3-post/types';
import { createAction, extractHashtagsFromText } from '@wix/communities-blog-client-common';
import { getCategoryIds } from '@app/external/common/store/categories/categories-selectors';
import { DEBOUNCE_CANCEL } from '../../common/middleware/redux-middlewares';
import { normalizePost } from '../../common/services/post-utils';
import type { NormalizedPost } from '../../common/types';
import type { FeedPageThunkAction } from '../../feed-page/types';
import {
  FETCH_SEARCH_DEBOUNCED,
  FETCH_SEARCH_FAILURE,
  FETCH_SEARCH_REQUEST,
  FETCH_SEARCH_SUCCESS,
} from './fetch-search-types';

const fetchSearchRequest = createAction(FETCH_SEARCH_REQUEST);
const fetchSearchSuccess = createAction(
  FETCH_SEARCH_SUCCESS,
  (payload: HttpResponse<QueryPostsResponse> & { posts: NormalizedPost[] }) => payload,
);
const fetchSearchFailure = createAction(FETCH_SEARCH_FAILURE);

const DEBOUNCE_WAIT = 300;

function fetchSearch(
  searchTerm: string,
): FeedPageThunkAction<Promise<HttpResponse<QueryPostsResponse>>> {
  return (dispatch, getState, { httpClient, errorHandler }) => {
    const { withErrorHandler, getResolvedError } = errorHandler;

    dispatch(fetchSearchRequest());

    const hashtags = extractHashtagsFromText(searchTerm);

    const query = {
      filter: {},
    } satisfies PlatformQuery;

    if (hashtags.length) {
      Object.assign(query.filter, {
        hashtags: {
          $hasSome: hashtags,
        },
      });
    } else {
      Object.assign(query.filter, {
        title: { $contains: encodeURIComponent(searchTerm) },
      });
    }

    return withErrorHandler(
      () =>
        httpClient.request(
          queryPosts({
            query,
            fieldsets: [PostFieldField.METRICS],
          }),
        ),
      {
        errorCodesMap: {},
      },
    )
      .then((response) => {
        const state = getState();

        const posts = response.data.posts ?? [];

        const normalizedPosts = posts.map((post) =>
          normalizePost({ post, blogCategoryIds: getCategoryIds(state), origin: '/v3/posts' }),
        );

        dispatch(
          fetchSearchSuccess({
            ...response,
            posts: normalizedPosts,
          }),
        );

        return response;
      })
      .catch((err) => {
        dispatch(fetchSearchFailure());
        const error = getResolvedError(err);
        throw error;
      });
  };
}

function fetchSearchDebounced(
  query: string,
  debounce: number = DEBOUNCE_WAIT,
): FeedPageThunkAction<Promise<HttpResponse<QueryPostsResponse>>> & {
  type: string;
  meta: { debounce: number };
} {
  const action = fetchSearch(query) as FeedPageThunkAction<
    Promise<HttpResponse<QueryPostsResponse>>
  > & {
    type: string;
    meta: { debounce: number };
  };
  action.type = FETCH_SEARCH_DEBOUNCED;
  action.meta = { debounce };
  return action;
}

export function fetchSearchWithCancel({
  query,
  debounce,
  minQueryLength,
}: {
  query: string;
  debounce?: number;
  minQueryLength: number;
}): ReturnType<typeof fetchSearchDebounced> | { type: string; payload: { type: string } } {
  if (query.length >= minQueryLength) {
    return fetchSearchDebounced(query, debounce);
  }
  return {
    type: DEBOUNCE_CANCEL,
    payload: {
      type: FETCH_SEARCH_DEBOUNCED,
    },
  };
}
